import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, CalendarDays, Clock3, User } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sanitize(html) {
  return DOMPurify.sanitize(html);
}

function renderMarkdown(text) {
  return text.split("\n\n").map((block, i) => {
    const blockId = `block-${block.slice(0, 20).replace(/\W/g, "")}-${i}`;
    if (block.startsWith("### ")) {
      return <h3 key={blockId} className="mt-10 mb-4 font-display text-xl font-semibold tracking-tight">{block.slice(4)}</h3>;
    }
    if (block.startsWith("## ")) {
      return <h2 key={blockId} className="mt-12 mb-5 font-display text-2xl font-semibold tracking-tight sm:text-3xl">{block.slice(3)}</h2>;
    }
    if (block.startsWith("- ")) {
      const items = block.split("\n").filter(l => l.startsWith("- "));
      return (
        <ul key={blockId} className="my-5 space-y-3 pl-1">
          {items.map((item, j) => (
            <li key={`${blockId}-li-${j}`} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span dangerouslySetInnerHTML={{ __html: sanitize(item.slice(2).replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')) }} />
            </li>
          ))}
        </ul>
      );
    }
    if (/^\d+\.\s/.test(block)) {
      const items = block.split("\n").filter(l => /^\d+\.\s/.test(l));
      return (
        <ol key={blockId} className="my-5 space-y-3 pl-1 list-none">
          {items.map((item, j) => (
            <li key={`${blockId}-ol-${j}`} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
              <span className="font-display text-sm font-semibold text-primary">{j + 1}.</span>
              <span dangerouslySetInnerHTML={{ __html: sanitize(item.replace(/^\d+\.\s*/, "").replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')) }} />
            </li>
          ))}
        </ol>
      );
    }
    return (
      <p
        key={blockId}
        className="my-5 text-base leading-relaxed text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: sanitize(block.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>').replace(/\u2014/g, "&mdash;")) }}
      />
    );
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-24" data-testid="blog-post-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 pt-24" data-testid="blog-post-not-found">
        <p className="text-lg text-muted-foreground">Article introuvable.</p>
        <Link to="/actualites" className="text-sm font-semibold text-primary transition-colors hover:text-foreground">
          Retour aux actualités
        </Link>
      </div>
    );
  }

  return (
    <article className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44" data-testid="blog-post-page">
      <SEO title={post.title} description={post.excerpt} path={`/actualites/${post.slug}`} type="article" />
      <div className="mx-auto max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
          <Link
            to="/actualites"
            className="group mb-10 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            data-testid="blog-post-back-link"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Toutes les actualités
          </Link>

          <Badge variant="outline" className="mb-6 border-primary/40 text-primary" data-testid="blog-post-category">
            {post.category}
          </Badge>

          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl" data-testid="blog-post-title">
            {post.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-muted-foreground" data-testid="blog-post-meta">
            <span className="inline-flex items-center gap-2">
              <User className="h-4 w-4" aria-hidden="true" />
              {post.author}
            </span>
            <span className="inline-flex items-center gap-2">
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
              {new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(new Date(post.published_at))}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock3 className="h-4 w-4" aria-hidden="true" />
              {post.read_time}
            </span>
          </div>

          <div className="my-10 h-px bg-border" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          data-testid="blog-post-content"
        >
          {renderMarkdown(post.content)}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-16 rounded-2xl border border-border/70 bg-card/60 p-8"
          data-testid="blog-post-cta"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-primary">Besoin d'accompagnement ?</p>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Intellix peut vous aider à mettre en place les solutions évoquées dans cet article.
          </p>
          <Link to="/contact" className="mt-5 inline-flex text-sm font-semibold text-primary transition-colors hover:text-foreground" data-testid="blog-post-contact-link">
            Nous contacter →
          </Link>
        </motion.div>
      </div>
    </article>
  );
}
